import { useRef, useState, useEffect } from "react";
import { Upload, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";

/**
 * Controls to manage a genre's art:
 *   • Upload PNG/JPEG (browser-resized to max 800px long side, JPEG q=0.8, data URL)
 *   • Paste image URL
 *   • Auto-select art toggle (uses first song's thumbnail when ON)
 *   • Preview + active-source label
 *
 * Props:
 *   value: { autoArt?: boolean, artImage?: string, image?: string | null }
 *     - autoArt: current auto flag (defaults to true when missing)
 *     - artImage: saved custom image (data URL or URL string)
 *     - image: effective image shown to users (nullable). Used only for preview.
 *   onChange: (partial) => void   — called with { autoArt?, artImage? } patches
 *   testIdPrefix: string          — unique prefix for data-testids (e.g. "new" or `g-${slug}`)
 */
export default function GenreArtControls({ value, onChange, testIdPrefix = "genre-art" }) {
  const fileRef = useRef(null);
  const autoArt = value?.autoArt !== false; // default true
  const artImage = value?.artImage || "";
  const [urlDraft, setUrlDraft] = useState(artImage);
  useEffect(() => { setUrlDraft(artImage); }, [artImage]);

  const preview = autoArt ? (value?.image || null) : (artImage || null);
  const sourceLabel = autoArt ? "Auto from song" : (artImage ? "Custom image" : "No art set");

  const resizeAndStore = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g)$/i.test(file.type)) {
      toast.error("Only PNG or JPEG images are allowed.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("That image is over 5 MB. Pick a smaller file.");
      return;
    }
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const MAX = 800;
          let { width, height } = img;
          if (width > height && width > MAX) { height = Math.round(height * (MAX / width)); width = MAX; }
          else if (height >= width && height > MAX) { width = Math.round(width * (MAX / height)); height = MAX; }
          const canvas = document.createElement("canvas");
          canvas.width = width; canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          try { resolve(canvas.toDataURL("image/jpeg", 0.8)); }
          catch (e) { reject(e); }
        };
        img.onerror = () => reject(new Error("Could not read that image"));
        const reader = new FileReader();
        reader.onload = () => { img.src = reader.result; };
        reader.onerror = () => reject(new Error("Could not read file"));
        reader.readAsDataURL(file);
      });
      onChange({ artImage: dataUrl });
      toast.success("Image resized and saved");
    } catch (e) {
      toast.error(e.message || "Could not process image");
    }
  };

  const applyUrl = () => {
    const u = urlDraft.trim();
    onChange({ artImage: u });
    toast.success(u ? "Image URL saved" : "Image cleared");
  };

  return (
    <div className="bg-black/20 border border-white/10 rounded-lg p-2 space-y-2" data-testid={`${testIdPrefix}-art-controls`}>
      <div className="flex items-center gap-3">
        <div className="w-14 h-14 shrink-0 rounded-md overflow-hidden border border-white/10 bg-zinc-800 flex items-center justify-center">
          {preview ? (
            <img src={preview} alt="" className="w-full h-full object-cover" data-testid={`${testIdPrefix}-art-preview`} />
          ) : (
            <span className="text-[9px] font-mono-ui text-zinc-500">no art</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-mono-ui uppercase tracking-widest text-zinc-500">Active source</div>
          <div className="text-xs font-medium" data-testid={`${testIdPrefix}-art-source`}>{sourceLabel}</div>
        </div>
        <label className="flex items-center gap-2 text-xs cursor-pointer select-none" title="When ON, uses the first song's thumbnail">
          <span className="text-zinc-400">Auto</span>
          <input
            type="checkbox"
            checked={autoArt}
            onChange={(e) => onChange({ autoArt: e.target.checked })}
            data-testid={`${testIdPrefix}-art-auto`}
            className="accent-cyan-400"
          />
        </label>
      </div>
      <div className="flex gap-1.5">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          data-testid={`${testIdPrefix}-art-upload-btn`}
          className="flex-1 px-2 py-1.5 rounded border border-white/10 hover:bg-white/5 text-xs flex items-center justify-center gap-1"
        >
          <Upload className="w-3 h-3" /> Add PNG/JPEG
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg"
          className="hidden"
          data-testid={`${testIdPrefix}-art-file-input`}
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) resizeAndStore(f); }}
        />
      </div>
      <div className="flex gap-1.5">
        <div className="relative flex-1">
          <LinkIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-500" />
          <input
            value={urlDraft}
            onChange={(e) => setUrlDraft(e.target.value)}
            placeholder="Image URL (e.g. R2 link)"
            data-testid={`${testIdPrefix}-art-url-input`}
            className="w-full bg-white/5 border border-white/10 rounded pl-7 pr-2 py-1.5 text-xs focus:outline-none focus:border-cyan-400/50"
          />
        </div>
        <button
          type="button"
          onClick={applyUrl}
          data-testid={`${testIdPrefix}-art-url-apply`}
          className="px-2 py-1.5 rounded bg-cyan-400 text-black text-xs font-semibold hover:bg-cyan-300"
        >
          Set
        </button>
      </div>
    </div>
  );
}
